// Cold calling (docs/specs/cold-calling.md). The external caller works one
// queue; owners see every list, every call and the numbers.

import { useState } from 'react';
import { useSession } from '../../auth/SessionProvider';
import { CALL_OUTCOMES, useColdStats } from '../../lib/data';
import { fDay, fShort } from '../../lib/format';
import { ErrorBox, useDrawer } from '../../ui/kit';
import { Icon } from '../../ui/icons';
import { Kpis } from './Kpis';
import { Queue } from './Queue';

const OUTCOME_LABEL = Object.fromEntries(CALL_OUTCOMES.map((o) => [o.id, o.label]));
const OUTCOME_PILL: Record<string, string> = {
  booked: 'te',
  not_interested: 'red',
  send_email: '',
  call_back: 'or',
};

export function ColdCalling() {
  const { me } = useSession();
  const [list, setList] = useState(false);
  if (me?.external || list)
    return <Queue onBack={me?.external ? undefined : () => setList(false)} />;
  return <Overview onList={() => setList(true)} />;
}

// Owners ---------------------------------------------------------------------------

function Overview({ onList }: { onList: () => void }) {
  const q = useColdStats();
  const { open } = useDrawer();
  const s = q.data;
  const hourMax = Math.max(1, ...(s?.hours ?? []).map((h) => h.dials));
  const outMax = Math.max(1, ...(s?.outcomes ?? []).map((o) => o.count));

  return (
    <>
      <div className="head">
        <div>
          <h1>
            Cold <span className="grad">calling</span>
          </h1>
          <p>
            One shared list for every caller. Import a sheet, see every call: who called, when, the
            result and the note.
          </p>
        </div>
        <div className="head-r">
          <button className="btn big" onClick={onList}>
            Contact list
          </button>
          <button className="btn primary big" onClick={() => open({ type: 'callImport' })}>
            Import list <Icon name="arrow" />
          </button>
        </div>
      </div>
      <ErrorBox error={q.error} />
      {!s ? (
        <section className="card sec loading">Loading…</section>
      ) : (
        <>
          <Kpis s={s} />
          <div className="two">
            <section className="card" data-testid="cc-hours">
              <div className="chead">
                <span className="lbl g">Connect rate by hour</span>
              </div>
              <p className="sub">Last 4 weeks, in the team's time zone.</p>
              {s.hours.length === 0 ? (
                <div className="empty">No calls logged yet.</div>
              ) : (
                <div className="bars">
                  {s.hours.map((h) => (
                    <div className="c" key={h.hour}>
                      <em>{h.dials ? Math.round((h.reached / h.dials) * 100) : 0}%</em>
                      <div className="b t" style={{ height: `${(h.dials / hourMax) * 100}%` }} />
                      <span>{h.hour}:00</span>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ height: 18 }} />
            </section>
            <section className="card">
              <div className="chead">
                <span className="lbl p">Outcomes today</span>
              </div>
              <div className="list-pad">
                {s.outcomes.length === 0 && <div className="empty">Nothing logged today.</div>}
                {s.outcomes.map((o) => (
                  <div className="hbar" key={o.outcome}>
                    <span>{OUTCOME_LABEL[o.outcome] ?? o.outcome}</span>
                    <i
                      style={{ width: `${(o.count / outMax) * 100}%`, background: 'var(--teal)' }}
                    />
                    <b>{o.count}</b>
                  </div>
                ))}
              </div>
            </section>
          </div>

          <section className="card sec" data-testid="cc-callers">
            <div className="chead">
              <span className="lbl g">Callers</span>
            </div>
            <div className="list-pad">
              {s.callers.length === 0 && <div className="empty">Nobody has called yet.</div>}
              {s.callers.map((c) => (
                <div className="trow" style={{ gridTemplateColumns: '1fr auto auto' }} key={c.name}>
                  <div className="t" style={{ fontSize: 16 }}>
                    {c.name}
                  </div>
                  <span className="m">{c.today} today</span>
                  <span className="m">{c.total} in all</span>
                </div>
              ))}
            </div>
          </section>

          <section className="card sec" data-testid="cc-lists">
            <div className="chead">
              <span className="lbl g">Call lists</span>
            </div>
            {s.lists.length === 0 ? (
              <div className="empty">No list yet. Import your first sheet.</div>
            ) : (
              <div className="scroll" tabIndex={0} role="region" aria-label="Call lists table">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>List</th>
                      <th className="num">Contacts</th>
                      <th className="num">Skipped</th>
                      <th>Progress</th>
                      <th className="num">Booked</th>
                    </tr>
                  </thead>
                  <tbody>
                    {s.lists.map((l) => (
                      <tr key={l.id}>
                        <td>
                          <b>{l.name}</b>
                          <div style={{ fontSize: 13, color: 'var(--muted)' }}>
                            {l.market}, imported {fDay(l.created_at)}
                          </div>
                        </td>
                        <td className="num">{l.imported}</td>
                        <td
                          className="num"
                          title="Already in the queue or pipeline, bad phone, a city with a client, do-not-contact"
                        >
                          {l.duplicate + l.bad_phone + l.city_blocked + l.blacklisted}
                        </td>
                        <td style={{ minWidth: 160 }}>
                          <div className="meter" style={{ margin: 0 }}>
                            <i
                              style={{
                                width: `${l.imported ? (l.called / l.imported) * 100 : 0}%`,
                              }}
                            />
                          </div>
                          <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 4 }}>
                            {l.called} of {l.imported} called, {l.done} finished
                          </div>
                        </td>
                        <td className="num">
                          <b>{l.booked}</b>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div style={{ height: 12 }} />
          </section>

          <section className="card sec">
            <div className="chead">
              <span className="lbl p">Latest calls</span>
            </div>
            <div className="list-pad">
              {s.latest.length === 0 && <div className="empty">No calls logged yet.</div>}
              {s.latest.map((x, i) => (
                <div className="trow" style={{ gridTemplateColumns: '1fr auto' }} key={i}>
                  <div>
                    <div className="t" style={{ fontSize: 16 }}>
                      {x.clinic}{' '}
                      <span style={{ fontWeight: 400, color: 'var(--muted)', fontSize: 14 }}>
                        {x.city}
                      </span>
                    </div>
                    {x.note && <div className="m">{x.note}</div>}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <span className={`pill sm ${OUTCOME_PILL[x.outcome] ?? 'gr'}`}>
                      {OUTCOME_LABEL[x.outcome] ?? x.outcome}
                    </span>
                    <div className="m" style={{ fontSize: 13, marginTop: 4 }}>
                      {fShort(x.at)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </>
      )}
    </>
  );
}
